const resolveApiUrl = () => {
  const fromEnv = String(process.env.REACT_APP_API_URL || '').trim();
  if (fromEnv) return fromEnv.replace(/\/+$/, '');

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return 'http://localhost:3000';
    }
  }

  return 'https://backend.angelfans.com.br';
};

const API_URL = resolveApiUrl();

const ACCESS_TOKEN_KEY = 'angel_access_token';
const REFRESH_TOKEN_KEY = 'angel_refresh_token';

function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function setTokens(accessToken, refreshToken) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

async function request(path, options = {}) {
  const withAuth = options.withAuth !== false;
  const token = getAccessToken();
  const headers = { ...(options.headers || {}) };
  delete options.withAuth;

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(withAuth && token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    const rawMessage = payload?.error?.message || payload?.message || 'Erro de requisição';
    const message = Array.isArray(rawMessage) ? rawMessage.join(', ') : String(rawMessage);

    if (response.status === 401) {
      if (withAuth) {
        // Authenticated request rejected — session is no longer valid
        clearTokens();
        throw new Error('Sessão expirada ou token inválido. Faça login novamente.');
      }
      // Unauthenticated request (login/register) rejected — preserve server message
      throw new Error(`[401] ${message}`);
    }

    throw new Error(`[${response.status}] ${message}`);
  }

  return response.status === 204 ? null : response.json().catch(() => null);
}

export const api = {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  async login(email, password) {
    return request('/auth/login', {
      method: 'POST',
      withAuth: false,
      body: JSON.stringify({ email, password })
    });
  },
  async loginViaAngelModel(email, password) {
    return request('/auth/login-angelmodel', {
      method: 'POST',
      withAuth: false,
      body: JSON.stringify({ email, password })
    });
  },
  async loginViaGoogle(idToken) {
    return request('/auth/login-google', {
      method: 'POST',
      withAuth: false,
      body: JSON.stringify({ idToken })
    });
  },
  async registerViaGoogle(idToken, role) {
    return request('/auth/register-google', {
      method: 'POST',
      withAuth: false,
      body: JSON.stringify({ idToken, role })
    });
  },
  async register(payload) {
    return request('/auth/register', {
      method: 'POST',
      withAuth: false,
      body: JSON.stringify(payload)
    });
  },
  async me() {
    return request('/users/me');
  },
  async updateMe(payload) {
    return request('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
  },
  async creators() {
    return request('/users/creators', { withAuth: false });
  },
  async creatorByUsername(username) {
    return request(`/users/creators/${encodeURIComponent(username)}`, { withAuth: false });
  },
  async subscriptions() {
    return request('/subscriptions/me');
  },
  async createSubscription(creatorId, amount) {
    return request('/subscriptions', {
      method: 'POST',
      body: JSON.stringify({ creatorId, amount })
    });
  },
  async cancelSubscription(creatorId) {
    return request(`/subscriptions/${creatorId}`, { method: 'DELETE' });
  },
  async creatorFeed(creatorId, options = {}) {
    return request(`/content/feed?creatorId=${creatorId}`, options);
  },
  async createPost(payload) {
    return request('/content/posts', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },
  async feed() {
    return request('/content/feed');
  },
  async toggleLike(postId) {
    return request(`/content/posts/${postId}/like`, { method: 'PATCH' });
  },
  async addComment(postId, text) {
    return request(`/content/posts/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ text })
    });
  },
  async notifications() {
    return request('/notifications/me');
  },
  async markAllRead() {
    return request('/notifications/me/read-all', { method: 'PATCH' });
  },
  async changePassword(currentPassword, newPassword) {
    return request('/auth/change-password', {
      method: 'PATCH',
      body: JSON.stringify({ currentPassword, newPassword })
    });
  }
};
